<!-- src/apps/secret/components/form/SecretForm.vue -->

<script setup lang="ts">
  import { useI18n } from 'vue-i18n';
  import BasicFormAlerts from '@/shared/components/forms/BasicFormAlerts.vue';
  import OIcon from '@/shared/components/icons/OIcon.vue';
  import { useDomainContext } from '@/shared/composables/useDomainContext';
  import { usePrivacyOptions } from '@/shared/composables/usePrivacyOptions';
  import { useSecretConcealer } from '@/shared/composables/useSecretConcealer';
  import { useBootstrapStore } from '@/shared/stores/bootstrapStore';
  import { useLocalReceiptStore } from '@/shared/stores/localReceiptStore';
  import {
    DEFAULT_BUTTON_TEXT_LIGHT,
    DEFAULT_CORNER_CLASS,
  } from '@/shared/stores/identityStore';
  import { NEUTRAL_BRAND_DEFAULTS } from '@/shared/constants/brand';
  import { type LocalReceipt } from '@/types/ui/local-receipt';
  import { nanoid } from 'nanoid';
  import { storeToRefs } from 'pinia';
  import { computed, ref, watch } from 'vue';
  import { useRouter } from 'vue-router';

  const { t } = useI18n();

  import { FIELD_ERROR, FIELD_SHELL } from './fieldStyles';
  import SecretContentInputArea from './SecretContentInputArea.vue';

  export interface Props {
    enabled?: boolean;
    withRecipient?: boolean;
    withAsterisk?: boolean;
    withGenerate?: boolean;
    withExpiry?: boolean;
    cornerClass?: string;
    primaryColor?: string;
    buttonTextLight?: boolean;
    /** When true, form stays on page after creation instead of navigating to receipt */
    workspaceMode?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {
    enabled: true,
    withRecipient: false,
    withAsterisk: false,
    withGenerate: false,
    withExpiry: true,
    cornerClass: DEFAULT_CORNER_CLASS,
    primaryColor: NEUTRAL_BRAND_DEFAULTS.primary_color,
    buttonTextLight: DEFAULT_BUTTON_TEXT_LIGHT,
    workspaceMode: false,
  });

  const emit = defineEmits<{
    /** Emitted after successful secret creation with the response data */
    (e: 'created', response: LocalReceipt): void;
  }>();

  const router = useRouter();
  const localReceiptStore = useLocalReceiptStore();
  const showProTip = ref(props.withAsterisk);

  // Get passphrase configuration for UI hints
  const bootstrapStore = useBootstrapStore();
  const {
    secret_options: secretOptions,
    domain_strategy: domainStrategy,
    display_domain: displayDomain,
  } = storeToRefs(bootstrapStore);

  const passphraseConfig = computed(() => secretOptions.value?.passphrase);
  const isPassphraseRequired = computed(() => passphraseConfig.value?.required || false);

  // Recipient field is shown only when the route opts in (withRecipient) AND the
  // ui.capabilities.recipient flag is not explicitly disabled. An unset flag
  // (undefined) is treated as enabled, matching the config default of true.
  const { uiCapabilities } = storeToRefs(bootstrapStore);
  const showRecipient = computed(
    () => props.withRecipient && uiCapabilities.value?.recipient !== false
  );

  // Helper function to get validation errors
  const getError = (field: keyof typeof form) => validation.errors.get(field);

  // Generate unique IDs for form fields to ensure proper label associations
  const uniqueId = computed(() => `secret-form-${Math.random().toString(36).substring(2, 9)}`);
  const passphraseId = computed(() => `passphrase-${uniqueId.value}`);
  const passphraseErrorId = computed(() => `passphrase-error-${uniqueId.value}`);
  const lifetimeId = computed(() => `lifetime-${uniqueId.value}`);
  const lifetimeErrorId = computed(() => `lifetime-error-${uniqueId.value}`);
  const recipientId = computed(() => `recipient-${uniqueId.value}`);
  const recipientErrorId = computed(() => `recipient-error-${uniqueId.value}`);

  const { form, validation, operations, isSubmitting, submit } = useSecretConcealer({
    onSuccess: async (response) => {
      if (!response) throw 'Response is missing';
      const newMessage: LocalReceipt = {
        id: nanoid(),
        receiptExtid: response.record.receipt.identifier,
        receiptShortid: response.record.receipt.shortid,
        secretExtid: response.record.secret.identifier,
        secretShortid: response.record.secret.shortid,
        shareDomain: response.record.share_domain,
        hasPassphrase: !!form.passphrase,
        ttl: form.ttl as number,
        createdAt: Date.now(),
      };
      // Add the message to the store
      localReceiptStore.addReceipt(newMessage);
      operations.reset();
      secretContentInput.value?.clearTextarea(); // Clear textarea

      // Emit event for parent components
      emit('created', newMessage);

      // In workspace mode, stay on page; otherwise navigate to receipt
      if (!props.workspaceMode) {
        router.push(`/receipt/${response.record.receipt.identifier}`);
      }
    },
  });

  const {
    state,
    lifetimeOptions,
    updatePassphrase,
    updateTtl,
    updateRecipient,
    togglePassphraseVisibility,
  } = usePrivacyOptions(operations);

  const { currentContext, isContextActive } = useDomainContext();

  // Compute whether the form has content or not
  const hasContent = computed(() => !!form.secret && (form.secret as string).trim().length > 0);

  // Form submission handlers
  const handleSubmit = () => {
    if (selectedAction.value === 'generate-password') {
      refreshGeneratedPasswords();
      return;
    }

    // Use appropriate submission type based on selected action
    return submit('conceal');
  };
  const secretContentInput = ref<{ clearTextarea: () => void } | null>(null);
  const selectedAction = ref<'create-link' | 'generate-password'>('create-link');
  const copiedPasswordId = ref<string | null>(null);

  interface GeneratedPassword {
    id: string;
    title: string;
    description: string;
    value: string;
  }

  const generatedPasswords = ref<GeneratedPassword[]>([]);

  const passwordWords = [
    'Alpin',
    'Blau',
    'Daten',
    'Fokus',
    'Klar',
    'Licht',
    'Nord',
    'Privat',
    'Sicher',
    'Signal',
    'Stark',
    'Wert',
  ];

  const randomInt = (max: number) => {
    const values = new Uint32Array(1);
    crypto.getRandomValues(values);
    return values[0] % max;
  };

  const pick = (characters: string) => characters[randomInt(characters.length)];

  const shuffle = (characters: string[]) => {
    const result = [...characters];
    for (let index = result.length - 1; index > 0; index -= 1) {
      const swapIndex = randomInt(index + 1);
      [result[index], result[swapIndex]] = [result[swapIndex], result[index]];
    }
    return result.join('');
  };

  const generateFromSets = (length: number, sets: string[]) => {
    const required = sets.map((set) => pick(set));
    const combined = sets.join('');
    const remaining = Array.from({ length: length - required.length }, () => pick(combined));
    return shuffle([...required, ...remaining]);
  };

  const generateReadablePassword = () => {
    const first = passwordWords[randomInt(passwordWords.length)];
    const second = passwordWords[randomInt(passwordWords.length)];
    const number = String(100 + randomInt(900));
    const symbol = pick('!-_#');
    return `${first}-${second}-${number}${symbol}`;
  };

  const generatePin = () => Array.from({ length: 6 }, () => randomInt(10)).join('');

  const refreshGeneratedPasswords = () => {
    generatedPasswords.value = [
      {
        id: 'strong',
        title: 'Starkes Passwort',
        description: '16 Zeichen mit Buchstaben, Zahlen und Symbolen.',
        value: generateFromSets(16, [
          'ABCDEFGHJKLMNPQRSTUVWXYZ',
          'abcdefghijkmnopqrstuvwxyz',
          '23456789',
          '!@#$%^&*',
        ]),
      },
      {
        id: 'readable',
        title: 'Lesbares Passwort',
        description: 'Einfacher zu tippen und trotzdem zufaellig.',
        value: generateReadablePassword(),
      },
      {
        id: 'long',
        title: 'Extra stark',
        description: '24 Zeichen fuer besonders sensible Zugriffe.',
        value: generateFromSets(24, [
          'ABCDEFGHJKLMNPQRSTUVWXYZ',
          'abcdefghijkmnopqrstuvwxyz',
          '23456789',
          '!@#$%^&*()-_=+',
        ]),
      },
      {
        id: 'pin',
        title: 'PIN-Code',
        description: '6-stelliger Zahlencode fuer kurze Freigaben.',
        value: generatePin(),
      },
    ];
    copiedPasswordId.value = null;
  };

  const copyGeneratedPassword = async (password: GeneratedPassword) => {
    await navigator.clipboard.writeText(password.value);
    copiedPasswordId.value = password.id;
    window.setTimeout(() => {
      if (copiedPasswordId.value === password.id) copiedPasswordId.value = null;
    }, 2000);
  };

  // Resolve the effective share domain for the form.
  // The domain context composable works for authenticated users but for
  // guest users on custom domains it falls back to canonical (no org
  // context). In that case, use display_domain from the bootstrap store.
  const effectiveShareDomain = computed(() => {
    const contextDomain = currentContext.value.domain;
    if (domainStrategy.value === 'custom' && displayDomain.value) {
      return displayDomain.value;
    }
    return contextDomain;
  });

  watch(
    effectiveShareDomain,
    (domain) => {
      if (domain) {
        operations.updateField('share_domain', domain);
      }
    },
    { immediate: true }
  );

  watch(selectedAction, (action) => {
    if (action === 'generate-password') {
      refreshGeneratedPasswords();
    }
  });

  // On a custom domain the form can only ever target that one domain, so the
  // "Creating links for <domain>" badge is noise. Only surface it where the
  // domain context is an actual choice (the workspace multi-domain case).
  const showDomainContext = computed(
    () => isContextActive.value && domainStrategy.value !== 'custom'
  );

  // Focus management when switching between Create Link and Generate Password modes
  const generatePasswordSection = ref<HTMLElement | null>(null);
</script>

<template>
  <div class="mx-auto min-w-[320px] max-w-2xl space-y-6">
    <!-- Enhanced Alert Display -->
    <BasicFormAlerts
      :errors="Array.from(validation.errors.values())"
      class="sticky top-4 z-50" />

    <form
      ref="form1"
      @submit.prevent="handleSubmit"
      :aria-busy="isSubmitting"
      class="space-y-6"
      data-testid="secret-form">
      <!-- prettier-ignore-attribute class -->
      <!--
        Card radius is fixed (rounded-lg), NOT the brand cornerClass. A large
        brand radius (now capped at xl / 64px since the 9999px `full` preset was
        removed) still reads as an over-rounded blob on this large container. The
        corner token belongs on the controls inside (inputs, select, button),
        matching BaseSecretDisplay's fixed-card / branded-controls split.
      -->
      <div
        ref="div1"
        class="overflow-visible rounded-lg border border-gray-200/50
          bg-gradient-to-br from-white to-gray-50/30
          shadow-[0_8px_30px_rgb(0,0,0,0.12),0_2px_8px_rgb(0,0,0,0.08)]
          backdrop-blur-sm
          dark:border-gray-700/50 dark:from-slate-900 dark:to-slate-800/30
          dark:shadow-[0_8px_30px_rgb(0,0,0,0.4),0_2px_8px_rgb(0,0,0,0.3)]">
        <div
          v-if="props.withGenerate"
          class="grid grid-cols-2 border-b border-gray-200/70 bg-white dark:border-gray-700/70 dark:bg-slate-900"
          role="tablist"
          aria-label="Secret action type">
          <button
            type="button"
            role="tab"
            :aria-selected="selectedAction === 'create-link'"
            class="flex items-center justify-center gap-2 px-4 py-4 text-sm font-semibold text-gray-600 outline-none hover:bg-[#0F4089] hover:text-white focus:outline-none dark:text-gray-300 dark:hover:bg-[#0F4089] dark:hover:text-white sm:text-base"
            :class="
              selectedAction === 'create-link'
                ? 'border-b-2 border-[#0F4089] bg-white text-[#0F4089] dark:bg-slate-900 dark:text-white'
                : 'border-b-2 border-transparent bg-gray-50 text-gray-500 dark:bg-slate-800 dark:text-gray-400'
            "
            @click="selectedAction = 'create-link'">
            <OIcon
              collection="heroicons"
              name="lock-closed"
              class="size-5"
              aria-hidden="true" />
            <span>Link erstellen</span>
          </button>
          <button
            type="button"
            role="tab"
            :aria-selected="selectedAction === 'generate-password'"
            class="flex items-center justify-center gap-2 px-4 py-4 text-sm font-semibold text-gray-600 outline-none hover:bg-[#0F4089] hover:text-white focus:outline-none dark:text-gray-300 dark:hover:bg-[#0F4089] dark:hover:text-white sm:text-base"
            :class="
              selectedAction === 'generate-password'
                ? 'border-b-2 border-[#0F4089] bg-white text-[#0F4089] dark:bg-slate-900 dark:text-white'
                : 'border-b-2 border-transparent bg-gray-50 text-gray-500 dark:bg-slate-800 dark:text-gray-400'
            "
            @click="selectedAction = 'generate-password'">
            <OIcon
              collection="heroicons"
              name="key"
              class="size-5"
              aria-hidden="true" />
            <span>Passwort generieren</span>
          </button>
        </div>

        <!-- Main Content Section -->
        <div class="p-8">
          <!-- Secret Input Section -->
          <span v-show="selectedAction === 'create-link'">
            <!--
              The secret content textarea is labelled by its own aria-label
              (web.secrets.enter_the_secret_content_here, set inside
              SecretContentInputArea). A separate sr-only <label> was removed
              because it had no associated control (invalid label semantics)
              and would have been overridden by that aria-label anyway.
            -->
            <SecretContentInputArea
              ref="secretContentInput"
              v-model:content="form.secret"
              :disabled="isSubmitting"
              :max-height="400"
              :corner-class="cornerClass"
              @update:content="(content) => operations.updateField('secret', content)" />
          </span>

          <!-- Generate Password Text -->
          <!-- Fixed rounded-lg (large container); see the outer card note above. -->
          <div
            v-show="selectedAction === 'generate-password'"
            class="rounded-lg border border-blue-100 bg-blue-50/40 p-5 dark:border-blue-900/40 dark:bg-slate-800/50"
            aria-labelledby="generatedPasswordHeader"
            aria-describedby="generatedPasswordDesc"
            role="region"
            ref="generatePasswordSection">
            <div class="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h4
                  id="generatedPasswordHeader"
                  class="text-lg font-semibold text-gray-900 dark:text-white"
                  tabindex="-1">
                  Passwort-Generator
                </h4>
                <p
                  id="generatedPasswordDesc"
                  class="mt-1 text-sm text-gray-600 dark:text-gray-300">
                  Vier sichere Passwortarten wurden automatisch erstellt.
                </p>
              </div>
              <button
                type="button"
                class="inline-flex items-center justify-center gap-2 rounded-lg border border-[#0F4089] bg-white px-4 py-2 text-sm font-semibold text-[#0F4089] outline-none hover:bg-[#0F4089] hover:text-white focus:outline-none"
                @click="refreshGeneratedPasswords">
                <OIcon
                  collection="heroicons"
                  name="arrow-path"
                  class="size-4"
                  aria-hidden="true" />
                <span>Neu generieren</span>
              </button>
            </div>

            <div class="grid gap-3">
              <div
                v-for="password in generatedPasswords"
                :key="password.id"
                class="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-slate-900">
                <div class="mb-3 flex items-start justify-between gap-3">
                  <div>
                    <h5 class="font-semibold text-gray-900 dark:text-white">
                      {{ password.title }}
                    </h5>
                    <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {{ password.description }}
                    </p>
                  </div>
                  <button
                    type="button"
                    class="inline-flex size-9 shrink-0 items-center justify-center rounded-md border border-gray-200 text-gray-700 outline-none hover:border-[#0F4089] hover:bg-[#0F4089] hover:text-white focus:outline-none dark:border-gray-700 dark:text-gray-200 dark:hover:border-[#0F4089] dark:hover:bg-[#0F4089] dark:hover:text-white"
                    :aria-label="`Passwort ${password.title} kopieren`"
                    @click="copyGeneratedPassword(password)">
                    <OIcon
                      collection="material-symbols"
                      :name="
                        copiedPasswordId === password.id ? 'check' : 'content-copy-outline'
                      "
                      class="size-5"
                      aria-hidden="true" />
                  </button>
                </div>
                <div
                  class="break-all rounded-md bg-gray-50 px-3 py-2 font-mono text-sm text-gray-900 dark:bg-slate-800 dark:text-gray-100">
                  {{ password.value }}
                </div>
                <p
                  v-if="copiedPasswordId === password.id"
                  class="mt-2 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                  Kopiert
                </p>
              </div>
            </div>
          </div>

          <!-- Form Controls Section -->
          <div
            v-show="selectedAction === 'create-link'"
            class="mt-6 grid gap-6 md:grid-cols-2 md:items-start">
            <!-- Passphrase Field -->
            <div class="relative">
              <label
                :for="passphraseId"
                class="mb-1 block font-brand text-sm text-gray-600 dark:text-gray-300">
                {{
                  isPassphraseRequired
                    ? 'Geheimnis mit Passwort schützen'
                    : 'Geheimnis mit Passwort schützen (optional)'
                }}
                <span
                  v-if="isPassphraseRequired"
                  class="ml-1 text-red-500"
                  aria-label="Required"
                  >*</span
                >
              </label>
              <!-- Fixed height container for hints to prevent layout shifts -->
              <div class="mb-2 min-h-4">
                <div
                  v-if="passphraseConfig"
                  class="text-xs text-gray-500 dark:text-gray-400">
                  <span v-if="passphraseConfig.minimum_length">
                    {{
                      t('web.secrets.passphraseMinimumLength', {
                        length: passphraseConfig.minimum_length,
                      })
                    }}
                  </span>
                  <span
                    v-if="passphraseConfig.minimum_length && passphraseConfig.enforce_complexity">
                    •
                  </span>
                  <span v-if="passphraseConfig.enforce_complexity">
                    {{ t('web.secrets.passphraseComplexityRequired') }}
                  </span>
                </div>
              </div>
              <div class="relative">
                <!-- prettier-ignore-attribute class -->
                <input
                  :type="state.passphraseVisibility ? 'text' : 'password'"
                  :value="form.passphrase"
                  :id="passphraseId"
                  name="passphrase"
                  autocomplete="off"
                  :aria-invalid="!!getError('passphrase')"
                  :aria-errormessage="getError('passphrase') ? passphraseErrorId : undefined"
                  :class="[cornerClass, FIELD_SHELL, getError('passphrase') ? FIELD_ERROR : '']"
                  class="py-2.5 pl-5 pr-10 text-gray-900 placeholder:text-gray-400 dark:placeholder:text-gray-500"
                  :placeholder="t('web.secrets.enterPassphrase')"
                  @input="(e) => updatePassphrase((e.target as HTMLInputElement).value)"
                  data-testid="secret-passphrase-input" />
                <!-- prettier-ignore-attribute class -->
                <button
                  type="button"
                  @click="togglePassphraseVisibility"
                  :aria-label="state.passphraseVisibility ? 'Hide passphrase' : 'Show passphrase'"
                  :aria-pressed="state.passphraseVisibility"
                  class="absolute inset-y-0 right-3 flex items-center
                    focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="secret-toggle-passphrase">
                  <OIcon
                    collection="heroicons"
                    :name="state.passphraseVisibility ? 'solid-eye' : 'outline-eye-off'"
                    class="size-4 text-gray-400 hover:text-gray-600"
                    aria-hidden="true" />
                </button>
              </div>
            </div>

            <!-- Expiry Selection -->
            <div
              v-if="props.withExpiry"
              class="relative">
              <label
                :for="lifetimeId"
                class="mb-1 block font-brand text-sm text-gray-600 dark:text-gray-300">
                {{ t('web.LABELS.expiration_time') || 'Secret Expiration' }}
              </label>
              <!-- Empty spacer to match passphrase field hint area -->
              <div class="mb-2 min-h-4"></div>
              <div class="relative">
                <!-- prettier-ignore-attribute class -->
                <select
                  :value="form.ttl"
                  :id="lifetimeId"
                  name="ttl"
                  :aria-invalid="!!getError('ttl')"
                  :aria-describedby="getError('ttl') ? lifetimeErrorId : undefined"
                  :class="[cornerClass, FIELD_SHELL, getError('ttl') ? FIELD_ERROR : '']"
                  class="appearance-none py-2.5 pl-5 pr-10 text-gray-600"
                  @change="(e) => updateTtl(Number((e.target as HTMLSelectElement).value))"
                  data-testid="secret-ttl-select">
                  <option
                    value=""
                    disabled>
                    {{ t('web.secrets.selectDuration') }}
                  </option>
                  <template v-if="lifetimeOptions.length > 0">
                    <option
                      v-for="option in lifetimeOptions"
                      :key="option.value"
                      :value="option.value">
                      {{ t('web.secrets.expiresIn', { duration: option.label }) }}
                    </option>
                  </template>
                  <option
                    v-else
                    value=""
                    disabled>
                    {{ t('web.UNITS.ttl.noOptionsAvailable') }}
                  </option>
                </select>
              </div>
            </div>
          </div>

          <!-- Recipient Field -->
          <div
            v-if="showRecipient"
            class="mt-6">
            <label
              :for="recipientId"
              class="mb-1 block font-brand text-sm text-gray-700 dark:text-gray-300">
              {{ t('web.COMMON.secret_recipient_address') || 'Email Recipient' }}
            </label>
            <div class="relative">
              <div class="pointer-events-none absolute inset-y-0 left-3 flex items-center">
                <OIcon
                  collection="heroicons"
                  name="envelope"
                  size="5"
                  class="text-gray-500 dark:text-gray-400"
                  aria-hidden="true" />
              </div>
              <!-- prettier-ignore-attribute class -->
              <input
                :id="recipientId"
                :value="form.recipient"
                type="email"
                name="recipient[]"
                autocomplete="email"
                :placeholder="t('web.COMMON.email_placeholder')"
                :aria-invalid="!!getError('recipient')"
                :aria-errormessage="getError('recipient') ? recipientErrorId : undefined"
                :class="[cornerClass, FIELD_SHELL, getError('recipient') ? FIELD_ERROR : '']"
                class="py-2.5 pl-10 pr-4 text-gray-900 placeholder:text-gray-400 dark:placeholder:text-gray-500"
                @input="(e) => updateRecipient((e.target as HTMLInputElement).value)"
                data-testid="secret-recipient-input" />
            </div>
          </div>
        </div>

        <!-- Pro tip Section -->
        <div
          v-if="showProTip && selectedAction === 'create-link'"
          class="relative flex items-start gap-3 overflow-hidden bg-gradient-to-r from-brandcomp-50/90 to-brandcomp-100/60 p-5 backdrop-blur-sm dark:from-brandcomp-900/30 dark:to-brandcomp-800/20">
          <!-- Decorative blur orb -->
          <div
            class="pointer-events-none absolute -bottom-8 -right-8 size-24 rounded-full bg-brandcomp-200/40 blur-2xl dark:bg-brandcomp-700/30"
            aria-hidden="true"></div>
          <OIcon
            collection="heroicons"
            name="information-circle"
            class="relative z-10 mt-0.5 size-5 shrink-0 text-brandcomp-600 dark:text-brandcomp-500" />
          <p class="relative z-10 text-sm text-brandcomp-700 dark:text-brandcomp-300">
            {{ t('web.homepage.protip1') }}
          </p>
        </div>

        <div
          v-show="selectedAction === 'create-link'"
          class="border-t border-gray-200/50 dark:border-gray-700/50">
          <!-- Actions Container -->
          <div class="p-8">
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between sm:gap-4">
              <!-- Domain Context Indicator -->
              <div
                v-if="showDomainContext"
                class="order-1 flex items-center gap-2 text-sm sm:order-1">
                <span class="text-gray-600 dark:text-gray-400">
                  {{ t('web.LABELS.creating_links_for') }}
                </span>
                <div
                  class="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-all duration-150"
                  :class="
                    currentContext.isCanonical
                      ? 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                      : 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300'
                  "
                  role="status"
                  :aria-label="
                    t('web.LABELS.scope_indicator', { domain: currentContext.displayName })
                  "
                  data-testid="secret-domain-context-indicator">
                  <OIcon
                    collection="heroicons"
                    :name="currentContext.isCanonical ? 'user-circle' : 'building-office'"
                    class="size-4"
                    aria-hidden="true" />
                  <span class="max-w-[180px] truncate">{{ currentContext.displayName }}</span>
                </div>
              </div>

              <!-- Action Button (full-width on mobile, normal width on desktop) -->
              <div class="order-2 shrink-0 sm:order-2 sm:ml-auto">
                <div class="mb-0 mt-3 sm:mt-0">
                  <button
                    type="submit"
                    class="inline-flex min-h-12 items-center justify-center gap-2 rounded-lg border px-6 py-3 text-base font-semibold outline-none hover:border-[#0F4089] hover:bg-[#0F4089] hover:text-white focus:outline-none disabled:cursor-not-allowed sm:min-w-48"
                    :class="
                      selectedAction === 'create-link'
                        ? 'border-[#0F4089] bg-white text-[#0F4089]'
                        : 'border-[#0F4089] bg-[#0F4089] text-white'
                    "
                    :disabled="selectedAction === 'create-link' && !hasContent"
                    :aria-label="
                      selectedAction === 'create-link' ? 'Create Secret Link' : 'Generate Password'
                    "
                    :aria-describedby="
                      selectedAction === 'create-link'
                        ? 'create-link-desc'
                        : 'generate-password-desc'
                    ">
                    <OIcon
                      collection="heroicons"
                      :name="selectedAction === 'create-link' ? 'lock-closed' : 'key'"
                      class="size-5"
                      aria-hidden="true" />
                    <span>
                      {{
                        selectedAction === 'create-link'
                          ? 'Link erstellen'
                          : 'Passwort generieren'
                      }}
                    </span>
                  </button>
                  <div
                    class="sr-only"
                    id="create-link-desc">
                    Creates a secure link to share your secret
                  </div>
                  <div
                    class="sr-only"
                    id="generate-password-desc">
                    Generates a secure random password
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </form>
  </div>
</template>
